<template>
  <div id="analytics-base" class="flex">
    <div
      id="analytics-panel"
      class="analytics-panel fixed left-0 z-50 w-60 flex-shrink-0 bg-pure-white border-r border-slate-400 overflow-y-auto"
    >
      <div
        class="sticky top-0 z-10 h-24 px-6 flex items-center bg-pure-white border-b border-slate-400"
      >
        <BlTextTitle3 text="Analytics" />
      </div>
      <div class="flex flex-col gap-1 px-3 py-1 pb-16">
        <div v-for="(tab, idx) in tabs" :key="idx">
          <div v-if="!tab.target" class="flex flex-col gap-1">
            <div
              v-if="tab.divider"
              class="h-0.5 my-1 w-full bg-slate-300"
            ></div>
            <div class="px-3">
              <BlTextBody3 :text="tab.label" class="text-slate-600" />
            </div>
          </div>
          <router-link
            v-else
            :to="tab.target"
            class="relative p-3 flex items-center gap-2 rounded transition-all duration-300"
            :class="[tab.isActive ? 'bg-slate-300' : 'hover:bg-hover-state']"
            :title="tab.label"
          >
            <BlIcon :icon="tab.icon" color="slate-6" class="h-4 w-4" />
            <BlTextSubtitle3 :text="tab.label" class="truncate" />
          </router-link>
        </div>
      </div>
    </div>

    <div id="analytics-body" class="pl-60 flex flex-col flex-grow">
      <CenterOverview v-if="!paramsTab && !searchParams['branch']" />
      <BranchOverview
        v-if="!paramsTab && searchParams['branch']"
        :key="`branch-overview-${searchParams['branch']}`"
      />
      <AcquisitionOverview v-if="paramsTab === 'acquisition-overview'" />
      <AcquisitionComparison v-if="paramsTab === 'acquisition-comparison'" />
      <ClientTurnover v-if="paramsTab === 'client-turnover'" />
      <TeamTurnover v-if="paramsTab === 'team-turnover'" />
      <TeamPerformance v-if="paramsTab === 'team-performance'" />
      <AppointmentOverview v-if="paramsTab === 'appointment-overview'" />
      <CancellationOverview v-if="paramsTab === 'cancellation-overview'" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import CenterOverview from "./CenterOverview.vue";
import BranchOverview from "./BranchOverview.vue";
import AcquisitionOverview from "./AcquisitionOverview.vue";
import AcquisitionComparison from "./AcquisitionComparison.vue";
import ClientTurnover from "./ClientTurnover.vue";
import TeamTurnover from "./TeamTurnover.vue";
import TeamPerformance from "./TeamPerformance.vue";
import AppointmentOverview from "./AppointmentOverview.vue";
import CancellationOverview from "./CancellationOverview.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody3 from "../BlTextBody3.vue";
import BlIcon from "../BlIcon.vue";

export default {
  name: "Analytics",
  components: {
    CenterOverview,
    BranchOverview,
    AcquisitionOverview,
    AcquisitionComparison,
    ClientTurnover,
    TeamTurnover,
    TeamPerformance,
    AppointmentOverview,
    CancellationOverview,
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody3,
    BlIcon
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "branches"]),
    enableAppointment() {
      return this.currentCenter.enable_appointment;
    },
    enableSalesPipeline() {
      return this.currentCenter.enable_sales_pipeline;
    },
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    enableAnalytics() {
      return this.currentCenter.enable_analytics;
    },
    searchParams() {
      return this.$route.query || {};
    },
    paramsTab() {
      return this.$route.params.tab;
    },
    tabs() {
      let mainDashboard = [
        { label: "MAIN DASHBOARD" },
        {
          target: "/analytics",
          isActive: !this.searchParams["branch"] && !this.paramsTab,
          icon: "location",
          label: this.enableBranch ? "All Branches" : "Center Overview"
        }
      ];
      if (this.enableBranch) {
        const arr = this.branches.map(i => ({
          target: `/analytics?branch=${i.id}`,
          isActive:
            this.searchParams["branch"] === i.id.toString() && !this.paramsTab,
          icon: "location",
          label: i.name
        }));
        mainDashboard = [...mainDashboard, ...arr];
      }

      const clientAcquisition = [
        { label: "CLIENT ACQUISITION", divider: true },
        {
          target: "/analytics/acquisition-overview",
          isActive: this.paramsTab === "acquisition-overview",
          icon: "user-square",
          label: "Acquisition Overview"
        },
        {
          target: "/analytics/acquisition-comparison",
          isActive: this.paramsTab === "acquisition-comparison",
          icon: "row-plus",
          label: "Acquisition Comparison"
        }
      ];

      const centerGrowth = [
        { label: "CENTER GROWTH", divider: true },
        {
          target: "/analytics/client-turnover",
          isActive: this.paramsTab === "client-turnover",
          icon: "user-square",
          label: "Client Turnover"
        },
        {
          target: "/analytics/team-turnover",
          isActive: this.paramsTab === "team-turnover",
          icon: "people-therapist",
          label: "Team Turnover"
        }
      ];

      const performanceAnalytics = [
        { label: "PERFORMANCE ANALYTICS", divider: true },
        {
          target: "/analytics/team-performance",
          isActive: this.paramsTab === "team-performance",
          icon: "people-therapist",
          label: "Team Performance"
        }
      ];

      const appointmentAnalytics = [
        { label: "APPOINTMENT ANALYTICS", divider: true },
        {
          target: "/analytics/appointment-overview",
          isActive: this.paramsTab === "appointment-overview",
          icon: "calendar-blank",
          label: "Appointment Overview"
        },
        {
          target: "/analytics/cancellation-overview",
          isActive: this.paramsTab === "cancellation-overview",
          icon: "calendar-x",
          label: "Cancellation Overview"
        }
      ];

      return [
        ...mainDashboard,
        ...(this.enableSalesPipeline ? clientAcquisition : []),
        ...centerGrowth,
        ...performanceAnalytics,
        ...appointmentAnalytics
      ];
    }
  },
  watch: {
    paramsTab(val, oldVal) {
      setTimeout(() => {
        if (val !== oldVal) this.onScrollToTop();
      }, 500);
    },
    searchParams(val, oldVal) {
      this.onScrollToLeft();
      if (val["branch"] !== oldVal["branch"]) this.onScrollToTop();
      else if (val["tab"] !== oldVal["tab"]) this.onScrollToTop();
    }
  },
  created() {
    this.getBranches({ params: "?sort=name_asc" }); // for get all branches (filter)
    this.getLabels({ params: "?sort=name_asc" }); // for get all tags (filter)
    this.getStages(); // for get all stages (filter)
    this.getCenterReasonSettings(); // for get all reasons (cancellation & rescheduling appointment)
  },
  mounted() {
    const app = document.getElementById("app");
    if (app) {
      app.classList.add("bg-slate-100");
      app.classList.remove("bg-slate-200");
    }
    if (!this.currentUser.role.includes("admin")) {
      return this.$router.push({ name: "Clients" });
    }
    if (!this.enableAnalytics) {
      return this.$router.push({ name: "Clients" });
    }
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) {
      app.scroll({ top: 0, left: 0 });
      app.classList.remove("bg-slate-100");
      app.classList.add("bg-slate-200");
    }
  },
  methods: {
    ...mapActions([
      "getBranches",
      "getLabels",
      "getStages",
      "getCenterReasonSettings"
    ]),
    onScrollToTop() {
      const app = document.getElementById("app");
      if (app) {
        app.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    onScrollToLeft() {
      const app = document.getElementById("app");
      if (app) {
        app.scrollTo({ left: 0, behavior: "smooth" });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.analytics-panel {
  height: calc(100vh - 4.5rem - 0.5rem);
}
</style>



// WEBPACK FOOTER //
// src/components/analytics/Analytics.vue